// /services/events.ts
import type {
  TypeEventSkeleton,
  TypeEventWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import formatAsContentfulDate from '~/utils/formatAsContentfulDate'

type FetchAccessibleEventIdsParams = {
  $client: ReturnType<typeof useNuxtApp>['$client']
  $userAccessCheck: ReturnType<typeof useNuxtApp>['$userAccessCheck']
  $visibility: ReturnType<typeof useNuxtApp>['$visibility']
  locale: string
  highestUserLevel: number
  searchQuery?: string
  brandId?: string
  productId?: string
  queryParams?: { [key: string]: unknown }
}

const resolveBrandFilterKey = (brand?: {
  sys?: { id?: string }
  fields?: { value?: string }
}) => brand?.fields?.value || brand?.sys?.id || ''

const resolveProductFilterKey = (product?: {
  sys?: { id?: string }
  fields?: { link?: string; name?: string; value?: string }
}) => product?.sys?.id || ''

export async function fetchAccessibleEventIds({
  $client,
  $userAccessCheck,
  $visibility,
  locale,
  highestUserLevel,
  searchQuery = '',
  brandId,
  productId,
  queryParams = {
    'fields.endDatetime[gte]': formatAsContentfulDate(new Date()), // future or ongoing
    'fields.showOnlyInCalendar[ne]': true,
  },
}: FetchAccessibleEventIdsParams) {
  const res =
    await $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
      content_type: 'event',
      locale,
      include: 10,
      limit: 1000,
      order: ['fields.startDatetime'],
      ...queryParams,
      'fields.title[match]': searchQuery,
      select: [
        'sys.id',
        'fields.visibility',
        'fields.accessLevel',
        'fields.associatedBrand',
        'fields.associatedProduct',
      ],
    })

  const accessible = res.items.filter((evt) => {
    const hasAccess = $userAccessCheck(
      evt.fields?.accessLevel,
      highestUserLevel,
    )
    const isVisible = $visibility(evt.fields?.visibility)
    return hasAccess && isVisible
  })

  const filtered = accessible.filter((evt) => {
    const brandFilterKey = resolveBrandFilterKey(evt?.fields?.associatedBrand)
    const productFilterKey = resolveProductFilterKey(
      evt?.fields?.associatedProduct as
        | {
            sys?: { id?: string }
            fields?: { link?: string; name?: string; value?: string }
          }
        | undefined,
    )
    const productName = evt?.fields?.associatedProduct?.fields?.name || ''
    const productLink = evt?.fields?.associatedProduct?.fields?.link || ''

    const brandOk = brandId
      ? evt?.fields?.associatedBrand?.sys?.id === brandId ||
        brandFilterKey === brandId
      : true
    const productOk = productId
      ? evt?.fields?.associatedProduct?.sys?.id === productId ||
        productFilterKey === productId ||
        productName === productId ||
        productLink === productId
      : true
    return brandOk && productOk
  })

  const entryIds = filtered.map((e) => e.sys.id)

  // Build filter options from all visible/accessible events (not post-filtered)
  const uniqueBrands: Record<string, string> = {}
  const uniqueProducts: Record<string, string> = {}

  for (const curr of accessible) {
    const b = curr.fields?.associatedBrand
    const brandKey = resolveBrandFilterKey(b)
    if (brandKey && b?.fields?.label) uniqueBrands[brandKey] = b.fields.label

    const p = curr.fields?.associatedProduct as
      | {
          sys?: { id?: string }
          fields?: { link?: string; name?: string; value?: string }
        }
      | undefined
    const productKey = resolveProductFilterKey(p)
    if (productKey && p?.fields?.name)
      uniqueProducts[productKey] = p.fields.name
  }

  const brands = Object.entries(uniqueBrands).map(([value, label]) => ({
    label,
    value,
  }))
  const products = Object.entries(uniqueProducts).map(([value, label]) => ({
    label,
    value,
  }))

  return { entryIds, brands, products }
}

export async function fetchEventsByIdsInBatches({
  $client,
  locale,
  ids,
  batchSize = 100,
}: {
  $client: ReturnType<typeof useNuxtApp>['$client']
  locale: string
  ids: string[]
  batchSize?: number
}) {
  if (!ids.length) return [] as TypeEventWithoutUnresolvableLinksResponse[]

  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += batchSize)
    chunks.push(ids.slice(i, i + batchSize))

  const pages = await Promise.all(
    chunks.map((chunk) =>
      $client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        locale,
        include: 10,
        limit: 1000,
        'sys.id[in]': chunk,
        order: ['fields.startDatetime'],
        select: [
          'sys',
          'fields.entryTitle',
          'fields.title',
          'fields.path',
          'fields.thumbnail',
          'fields.featuredImage',
          'fields.startDatetime',
          'fields.endDatetime',
        ],
      }),
    ),
  )

  return pages
    .flatMap((p) => p.items)
    .filter(Boolean) as TypeEventWithoutUnresolvableLinksResponse[]
}

// ---- sorting ----
const toTs = (iso?: string): number => {
  if (!iso) return NaN
  const t = Date.parse(iso)
  return Number.isNaN(t) ? NaN : t
}

const isOngoing = (now: number, s: number, e: number) =>
  Number.isFinite(s) && Number.isFinite(e) && s <= now && now <= e

const isUpcoming = (now: number, s: number) => Number.isFinite(s) && s > now
const isPast = (now: number, e: number) => Number.isFinite(e) && e < now

const tieBreak = (
  a: TypeEventWithoutUnresolvableLinksResponse,
  b: TypeEventWithoutUnresolvableLinksResponse,
) =>
  (a.fields.title || '').localeCompare(b.fields.title || '', undefined, {
    sensitivity: 'base',
  }) || a.sys.id.localeCompare(b.sys.id)

export function sortEventsByUrgency(
  events: TypeEventWithoutUnresolvableLinksResponse[],
  nowDate: Date = new Date(),
) {
  const now = nowDate.getTime()
  const enriched = events.map((e) => {
    const s = toTs(e.fields.startDatetime)
    const nd = toTs(e.fields.endDatetime)
    const status = isOngoing(now, s, nd)
      ? 'ongoing'
      : isUpcoming(now, s)
        ? 'upcoming'
        : isPast(now, nd)
          ? 'past'
          : 'unknown'
    return { e, s, nd, status }
  })

  const ongoing = enriched
    .filter((x) => x.status === 'ongoing')
    .sort((a, b) => a.nd - b.nd || a.s - b.s || tieBreak(a.e, b.e))

  const upcoming = enriched
    .filter((x) => x.status === 'upcoming')
    .sort((a, b) => a.s - b.s || a.nd - b.nd || tieBreak(a.e, b.e))

  const unknown = enriched
    .filter((x) => x.status === 'unknown')
    .sort((a, b) => tieBreak(a.e, b.e))

  return [...ongoing, ...upcoming, ...unknown].map((x) => x.e)
}
